/**
 * Individual document row in the eFolder tree view
 * Shows document title, status, file size, and download button
 */

import React from 'react';
import { FileText, Download, ExternalLink, CheckCircle2, Clock, AlertCircle, File, Archive, Trash2, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { formatFileSize, formatRelativeTime } from '@/services/encompassDocumentService';
import type { EncompassAttachment } from '@/types/encompass-documents';
import { HighlightedSnippet } from './HighlightedSnippet';

interface EfolderDocumentRowProps {
  attachment: EncompassAttachment;
  isSelected: boolean;
  onSelect: (attachment: EncompassAttachment) => void;
  onView: (attachment: EncompassAttachment) => void;
  onDownload: (attachment: EncompassAttachment) => void;
  /** Whether copy mode is active (shows checkboxes) */
  isCopyMode?: boolean;
  /** Whether this document is checked for copying */
  isChecked?: boolean;
  /** Callback when checkbox is toggled */
  onToggleCheck?: (attachmentId: string) => void;
  /** Current search query — used to highlight matched substrings in the title. */
  searchQuery?: string;
}

/**
 * Renders a title with case-insensitive substring matches wrapped in <mark>.
 * Used when the eFolder search matched the file name. Splits on a literal
 * substring (not a regex), so query chars like "(" or "." don't break it.
 */
function HighlightedTitle({ title, query }: { title: string; query: string }) {
  const trimmed = query.trim();
  if (!trimmed) {
    return <>{title}</>;
  }
  const lowerTitle = title.toLowerCase();
  const lowerQuery = trimmed.toLowerCase();
  const segments: React.ReactNode[] = [];
  let cursor = 0;
  let idx = lowerTitle.indexOf(lowerQuery, cursor);
  let key = 0;
  while (idx !== -1) {
    if (idx > cursor) {
      segments.push(<React.Fragment key={`t-${key++}`}>{title.slice(cursor, idx)}</React.Fragment>);
    }
    segments.push(
      <mark key={`m-${key++}`} className="bg-yellow-100 text-foreground rounded px-0.5">
        {title.slice(idx, idx + trimmed.length)}
      </mark>
    );
    cursor = idx + trimmed.length;
    idx = lowerTitle.indexOf(lowerQuery, cursor);
  }
  if (cursor < title.length) {
    segments.push(<React.Fragment key={`t-${key++}`}>{title.slice(cursor)}</React.Fragment>);
  }
  return <>{segments}</>;
}

/**
 * Get file icon based on file type/name and active status
 */
function getFileIcon(attachment: EncompassAttachment) {
  // Show archive icon for inactive (old version) documents
  if (attachment.is_active === false) {
    return <Archive className="h-4 w-4 text-muted-foreground/60" />;
  }

  const title = attachment.title?.toLowerCase() || '';

  if (title.endsWith('.pdf')) {
    return <FileText className="h-4 w-4 text-red-500" />;
  }
  if (/\.(jpg|jpeg|png|gif|tiff|tif|webp|bmp)$/i.test(title)) {
    return <File className="h-4 w-4 text-blue-500" />;
  }
  return <File className="h-4 w-4 text-muted-foreground" />;
}

/**
 * Get status indicator element
 */
function getStatusIndicator(attachment: EncompassAttachment) {
  const isTdSkipped = attachment.download_error?.includes('Transferred Data');

  switch (attachment.download_status) {
    case 'downloaded':
      return (
        <span className="flex items-center justify-center w-5 h-5 rounded-full bg-green-100" title="Ready">
          <CheckCircle2 className="h-3 w-3 text-green-600" />
        </span>
      );
    case 'pending':
      return (
        <span className="flex items-center justify-center w-5 h-5 rounded-full bg-yellow-100" title="Pending sync">
          <Clock className="h-3 w-3 text-yellow-600" />
        </span>
      );
    case 'pending_worker':
      return (
        <span className="flex items-center justify-center w-5 h-5 rounded-full bg-blue-100" title="Processing">
          <Clock className="h-3 w-3 text-blue-600" />
        </span>
      );
    case 'failed':
      return (
        <span className="flex items-center justify-center w-5 h-5 rounded-full bg-red-100" title="Sync failed">
          <AlertCircle className="h-3 w-3 text-red-600" />
        </span>
      );
    case 'skipped':
      return (
        <span
          className="flex items-center justify-center w-5 h-5 rounded-full bg-gray-100"
          title={isTdSkipped ? "Skipped – Transferred Data" : "Skipped (unsupported)"}
        >
          <File className="h-3 w-3 text-gray-500" />
        </span>
      );
    case 'unavailable':
      return (
        <span className="flex items-center justify-center w-5 h-5 rounded-full bg-gray-100" title="Unavailable">
          <AlertCircle className="h-3 w-3 text-gray-400" />
        </span>
      );
    case 'deleted':
      return (
        <span className="flex items-center justify-center w-5 h-5 rounded-full bg-gray-100" title="Deleted from Encompass">
          <Trash2 className="h-3 w-3 text-gray-500" />
        </span>
      );
    default:
      return null;
  }
}

export function EfolderDocumentRow({
  attachment,
  isSelected,
  onSelect,
  onView,
  onDownload,
  isCopyMode = false,
  isChecked = false,
  onToggleCheck,
  searchQuery = ''
}: EfolderDocumentRowProps) {
  const matchInfo = attachment.matchInfo;
  const matchQuery = searchQuery;

  const handleClick = () => {
    onSelect(attachment);
  };

  const handleView = (e: React.MouseEvent) => {
    e.stopPropagation();
    onView(attachment);
  };

  const handleDownload = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDownload(attachment);
  };

  const handleCheckboxChange = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onToggleCheck && canBeCopied) {
      onToggleCheck(attachment.id);
    }
  };

  const isInactive = attachment.is_active === false;
  // Only downloaded, active documents can be copied
  const canBeCopied = attachment.download_status === 'downloaded' && attachment.is_active !== false;

  return (
    <div
      className={cn(
        'group flex items-center gap-2 px-3 py-2 cursor-pointer rounded-md transition-all',
        'hover:bg-muted/60',
        isSelected && 'bg-primary/10 ring-1 ring-primary/30',
        isInactive && 'opacity-60',
        isCopyMode && isChecked && 'bg-primary/5'
      )}
      onClick={handleClick}
    >
      {/* Checkbox for copy mode */}
      {isCopyMode && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span onClick={handleCheckboxChange} className="flex-shrink-0">
                <Checkbox
                  checked={isChecked}
                  disabled={!canBeCopied}
                  className={cn(!canBeCopied && 'opacity-40')}
                />
              </span>
            </TooltipTrigger>
            {!canBeCopied && (
              <TooltipContent side="right">
                <p>{
                  attachment.download_status === 'pending' ? 'File still syncing' :
                  attachment.download_status === 'pending_worker' ? 'File being processed' :
                  attachment.download_status === 'failed' ? 'File sync failed' :
                  attachment.download_status === 'skipped' ? (
                    attachment.download_error?.includes('Transferred Data')
                      ? 'Transferred Data loan'
                      : 'File type not supported'
                  ) :
                  attachment.download_status === 'unavailable' ? 'File not available' :
                  attachment.download_status === 'deleted' ? 'File deleted from Encompass' :
                  attachment.is_active === false ? 'Old version cannot be copied' :
                  'Cannot copy this file'
                }</p>
              </TooltipContent>
            )}
          </Tooltip>
        </TooltipProvider>
      )}

      {/* File icon */}
      <span className="flex-shrink-0">
        {getFileIcon(attachment)}
      </span>

      {/* Document info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className={cn(
            'text-sm truncate',
            isSelected ? 'font-medium' : 'font-normal',
            isInactive && 'text-muted-foreground line-through'
          )}>
            {matchInfo?.matchedInTitle && attachment.title
              ? <HighlightedTitle title={attachment.title} query={matchQuery} />
              : (attachment.title || 'Untitled')}
          </span>
          {isInactive && (
            <Badge variant="outline" className="h-4 px-1 text-[10px] text-muted-foreground border-muted-foreground/30">
              Old
            </Badge>
          )}
        </div>

        <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
          {attachment.file_size && (
            <span>{formatFileSize(attachment.file_size)}</span>
          )}
          {attachment.uploaded_date && (
            <>
              {attachment.file_size && <span>•</span>}
              <span>{formatRelativeTime(attachment.uploaded_date)}</span>
            </>
          )}
        </div>

        {/* Content-search result: indicator + page + snippet. Only renders when
            the loan-scoped FTS RPC returned a hit for this attachment. */}
        {matchInfo?.matchedInContent && (
          <div className="flex items-start gap-1.5 mt-1 text-xs">
            <Search className="h-3 w-3 mt-0.5 text-amber-600 flex-shrink-0" aria-hidden="true" />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5 mb-0.5">
                <span className="text-[10px] uppercase tracking-wide text-amber-700 font-medium">Text match</span>
                {matchInfo.pageNumber !== null && matchInfo.pageNumber !== undefined && (
                  <Badge variant="outline" className="h-4 px-1 text-[10px] text-amber-700 border-amber-300 bg-amber-50">
                    Page {matchInfo.pageNumber}
                  </Badge>
                )}
              </div>
              {matchInfo.snippet && (
                <div className="line-clamp-2 break-words leading-snug">
                  <HighlightedSnippet snippet={matchInfo.snippet} />
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Status indicator */}
      <span className="flex-shrink-0">
        {getStatusIndicator(attachment)}
      </span>

      {/* View + Save buttons */}
      {attachment.download_status === 'downloaded' && attachment.storage_path && (
        isSelected ? (
          <div className="flex items-center gap-1 flex-shrink-0">
            <Button
              variant="outline"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={handleView}
              title="View in new tab"
            >
              <ExternalLink className="h-3.5 w-3.5 mr-1" />
              View
            </Button>
            <Button
              variant="default"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={handleDownload}
              title="Download file"
            >
              <Download className="h-3.5 w-3.5 mr-1" />
              Save
            </Button>
          </div>
        ) : (
          <div className="flex items-center gap-0.5 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0"
              onClick={handleView}
              title="View in new tab"
            >
              <ExternalLink className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0"
              onClick={handleDownload}
              title="Download file"
            >
              <Download className="h-3.5 w-3.5" />
            </Button>
          </div>
        )
      )}
    </div>
  );
}
